import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { KeyRound, Loader2, Pencil, Plus, Power, Trash2, X } from 'lucide-react'
import {
  fetchMembers, createMember, updateMember, deleteMember, resetMemberPassword, fetchFirms,
  Member, MemberCreate, FirmSummary,
} from '../../../lib/api/api'
import { useAuth, Role } from '../../../contexts/AuthContext'

type FormState = { email: string; full_name: string; role: string; password: string; firm_id: string }
const emptyForm: FormState = { email: '', full_name: '', role: '', password: '', firm_id: '' }

function errorDetail(error: any, fallback: string): string {
  const detail = error?.response?.data?.detail
  if (typeof detail === 'string' && detail) return detail
  return fallback
}

export default function MembersTab() {
  const { t } = useTranslation()
  const { user, isSuperAdmin } = useAuth()

  // A firm admin may only create/promote to professional or assistant; the
  // super admin may also create firm admins and must pick the firm.
  const assignableRoles: Role[] = isSuperAdmin ? ['professional', 'assistant', 'firm_admin'] : ['professional', 'assistant']

  const [members, setMembers] = useState<Member[]>([])
  const [firms, setFirms] = useState<FirmSummary[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [reloadToken, setReloadToken] = useState(0)

  const [isFormOpen, setIsFormOpen] = useState(false)
  const [editingMember, setEditingMember] = useState<Member | null>(null)
  const [form, setForm] = useState<FormState>(emptyForm)
  const [isSaving, setIsSaving] = useState(false)

  const [generatedPassword, setGeneratedPassword] = useState<{ email: string; password: string } | null>(null)

  useEffect(() => {
    if (!isSuperAdmin) return
    fetchFirms().then((res) => setFirms(res.data.firms)).catch(() => {})
  }, [isSuperAdmin])

  useEffect(() => {
    let active = true
    setIsLoading(true)
    setError(null)
    fetchMembers()
      .then((res) => { if (active) setMembers(res.data.members) })
      .catch(() => { if (active) setError(t('members.errors.loadFailed')) })
      .finally(() => { if (active) setIsLoading(false) })
    return () => { active = false }
  }, [reloadToken, t])

  const isSelf = (member: Member) => !!user?.email && member.email.toLowerCase() === user.email.toLowerCase()
  // Same rule as the server (FirmAccess.can_manage_user): nobody manages themselves or a
  // super admin, and a firm admin manages only the roles below theirs (not another firm admin)
  const canManage = (member: Member) =>
    !isSelf(member) && member.role !== 'super_admin' && (isSuperAdmin || member.role !== 'firm_admin')

  const openCreate = () => {
    setEditingMember(null)
    setForm({ ...emptyForm, role: assignableRoles[0], firm_id: firms[0]?.id || '' })
    setIsFormOpen(true)
  }

  const openEdit = (member: Member) => {
    setEditingMember(member)
    setForm({ email: member.email, full_name: member.full_name || '', role: member.role, password: '',
              firm_id: member.firm_id || '' })
    setIsFormOpen(true)
  }

  const closeForm = () => {
    setIsFormOpen(false)
    setEditingMember(null)
    setForm(emptyForm)
  }

  const handleSubmit = async () => {
    if (!form.email.trim()) return
    setIsSaving(true)
    setError(null)
    try {
      if (editingMember) {
        await updateMember(editingMember.id, {
          email: form.email, full_name: form.full_name || undefined, role: form.role,
        })
      } else {
        const body: MemberCreate = { email: form.email, role: form.role }
        if (form.full_name.trim()) body.full_name = form.full_name
        if (form.password.trim()) body.password = form.password
        if (isSuperAdmin) body.firm_id = form.firm_id
        const res = await createMember(body)
        const created = res.data as Member & { generated_password?: string }
        if (created.generated_password) {
          setGeneratedPassword({ email: created.email, password: created.generated_password })
        }
      }
      closeForm()
      setReloadToken((v) => v + 1)
    } catch (err) {
      setError(errorDetail(err, t('members.errors.saveFailed')))
    } finally {
      setIsSaving(false)
    }
  }

  const handleToggleActive = async (member: Member) => {
    setError(null)
    try {
      await updateMember(member.id, { is_active: !member.is_active })
      setReloadToken((v) => v + 1)
    } catch (err) {
      setError(errorDetail(err, t('members.errors.toggleFailed')))
    }
  }

  const handleResetPassword = async (member: Member) => {
    if (!window.confirm(t('members.list.confirmResetPassword', { email: member.email }))) return
    setError(null)
    try {
      const res = await resetMemberPassword(member.id)
      setGeneratedPassword({ email: member.email, password: res.data.new_password })
    } catch (err) {
      setError(errorDetail(err, t('members.errors.resetPasswordFailed')))
    }
  }

  const handleDelete = async (member: Member) => {
    if (!window.confirm(t('members.list.confirmDelete', { email: member.email }))) return
    setError(null)
    setSuccess(null)
    try {
      const res = await deleteMember(member.id)
      setSuccess(res.data.message)
      setReloadToken((v) => v + 1)
    } catch (err) {
      setError(errorDetail(err, t('members.errors.deleteFailed')))
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold">{t('members.title')}</h2>
        <button
          type="button"
          onClick={openCreate}
          className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-primary-600 text-white text-sm"
        >
          <Plus size={16} />
          {t('members.new')}
        </button>
      </div>

      {error && <p className="text-red-600 text-sm" role="alert">{error}</p>}
      {success && <p className="text-green-700 text-sm" role="status">{success}</p>}

      {isLoading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="animate-spin text-gray-400" />
        </div>
      ) : members.length === 0 ? (
        <p className="text-gray-500 text-center py-8">{t('members.list.noResults')}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 border-b">
                <th className="py-2 pr-4">{t('members.list.email')}</th>
                <th className="py-2 pr-4">{t('members.list.fullName')}</th>
                <th className="py-2 pr-4">{t('members.list.role')}</th>
                {isSuperAdmin && <th className="py-2 pr-4">{t('members.list.firm')}</th>}
                <th className="py-2 pr-4">{t('members.list.status')}</th>
                <th className="py-2 pr-4">{t('members.list.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {members.map((member) => (
                <tr key={member.id} className="border-b border-gray-100">
                  <td className="py-2 pr-4">{member.email}</td>
                  <td className="py-2 pr-4">{member.full_name || ''}</td>
                  <td className="py-2 pr-4">{t(`roles.${member.role}`)}</td>
                  {isSuperAdmin && <td className="py-2 pr-4">{member.firm_name || ''}</td>}
                  <td className="py-2 pr-4">
                    {member.is_active ? t('members.list.active') : t('members.list.inactive')}
                  </td>
                  <td className="py-2 pr-4">
                    {canManage(member) && (
                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          onClick={() => openEdit(member)}
                          title={t('members.actions.edit')}
                          aria-label={t('members.actions.edit')}
                          className="p-1.5 rounded-full text-gray-400 hover:text-primary-600 hover:bg-primary-50"
                        >
                          <Pencil size={16} />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleToggleActive(member)}
                          title={member.is_active ? t('members.actions.deactivate') : t('members.actions.activate')}
                          aria-label={member.is_active ? t('members.actions.deactivate') : t('members.actions.activate')}
                          className="p-1.5 rounded-full text-gray-400 hover:text-primary-600 hover:bg-primary-50"
                        >
                          <Power size={16} />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleResetPassword(member)}
                          title={t('members.actions.resetPassword')}
                          aria-label={t('members.actions.resetPassword')}
                          className="p-1.5 rounded-full text-gray-400 hover:text-primary-600 hover:bg-primary-50"
                        >
                          <KeyRound size={16} />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(member)}
                          title={t('members.actions.delete')}
                          aria-label={t('members.actions.delete')}
                          className="p-1.5 rounded-full text-gray-400 hover:text-red-600 hover:bg-red-50"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {generatedPassword && (
        <div role="status" className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="font-medium text-yellow-900">{t('members.generatedPassword.title')}</p>
              <p className="text-sm text-yellow-800 mt-1">{t('members.generatedPassword.hint')}</p>
              <p className="mt-2 text-sm">
                {generatedPassword.email}: <code className="bg-white px-2 py-0.5 rounded border">{generatedPassword.password}</code>
              </p>
            </div>
            <button
              type="button"
              onClick={() => setGeneratedPassword(null)}
              className="px-3 py-1.5 rounded-md border text-sm flex-shrink-0"
            >
              {t('members.generatedPassword.dismiss')}
            </button>
          </div>
        </div>
      )}

      {isFormOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] flex flex-col">
            <div className="flex justify-between items-center p-4 border-b">
              <h3 className="text-lg font-semibold">
                {editingMember ? t('members.form.editTitle') : t('members.form.createTitle')}
              </h3>
              <button type="button" onClick={closeForm} className="text-gray-500 hover:text-gray-700">
                <X size={18} />
              </button>
            </div>
            <div className="p-4 space-y-3 overflow-y-auto">
              <label className="block text-sm">
                <span className="text-gray-600">{t('members.form.emailLabel')}</span>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="mt-1 w-full border rounded-md px-3 py-1.5"
                />
              </label>
              <label className="block text-sm">
                <span className="text-gray-600">{t('members.form.fullNameLabel')}</span>
                <input
                  type="text"
                  value={form.full_name}
                  onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                  className="mt-1 w-full border rounded-md px-3 py-1.5"
                />
              </label>
              <label className="block text-sm">
                <span className="text-gray-600">{t('members.form.roleLabel')}</span>
                <select
                  value={form.role}
                  onChange={(e) => setForm({ ...form, role: e.target.value })}
                  className="mt-1 w-full border rounded-md px-3 py-1.5"
                >
                  {assignableRoles.map((role) => (
                    <option key={role} value={role}>{t(`roles.${role}`)}</option>
                  ))}
                </select>
              </label>
              {isSuperAdmin && !editingMember && (
                <label className="block text-sm">
                  <span className="text-gray-600">{t('members.form.firmLabel')}</span>
                  <select
                    value={form.firm_id}
                    onChange={(e) => setForm({ ...form, firm_id: e.target.value })}
                    className="mt-1 w-full border rounded-md px-3 py-1.5"
                  >
                    <option value="">{t('members.form.firmPlaceholder')}</option>
                    {firms.map((firm) => (
                      <option key={firm.id} value={firm.id}>{firm.name}</option>
                    ))}
                  </select>
                </label>
              )}
              {!editingMember && (
                <label className="block text-sm">
                  <span className="text-gray-600">{t('members.form.passwordLabel')}</span>
                  <input
                    type="text"
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    className="mt-1 w-full border rounded-md px-3 py-1.5"
                  />
                </label>
              )}
            </div>
            <div className="flex justify-end gap-2 p-4 border-t">
              <button type="button" onClick={closeForm} className="px-3 py-1.5 rounded-md border text-sm">
                {t('common.cancel')}
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={isSaving}
                className="px-3 py-1.5 rounded-md bg-primary-600 text-white text-sm disabled:opacity-50"
              >
                {t('common.save')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
